import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle } from 'lucide-react';
import { PageSEO } from '../components/SEO';

const Rechtliches = () => {
  return (
    <>
      <PageSEO 
        title="Impressum, Datenschutz & AGB | Firmbay"
        description="Rechtliche Informationen zu Firmbay: Impressum, Datenschutzerklärung und Allgemeine Geschäftsbedingungen. Ein Angebot der Interpohl Solutions GmbH."
        path="/rechtliches"
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-40 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Rechtliche
            <span className="block mt-2 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Informationen
            </span>
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Wichtiger Hinweis / Disclaimer */}
          <Card className="border-2 border-yellow-200 bg-yellow-50 shadow-md">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center space-x-2">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
                <span>Wichtiger Hinweis</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-800">
              <p className="font-medium">
                Wir bieten keine Rechts- oder Steuerberatung an und verkaufen keine Gesellschaften.
              </p>
              <p>
                Die Plattform dient der Vermittlung und organisatorischen Begleitung von GmbH-Gründungen, 
                Vorratsgesellschaften sowie Gesellschafterübertragungen.
              </p>
              <p>
                Sämtliche gesellschaftsrechtlichen Vorgänge erfolgen ausschließlich durch Notare und externe, 
                eigenverantwortliche Partner.
              </p>
            </CardContent>
          </Card>

          {/* Impressum */}
          <Card id="impressum" className="border-2 border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="text-2xl">Impressum</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Angaben gemäß § 5 DDG</h3>
                <p>
                  Interpohl Solutions GmbH<br />
                  Hansastr. 72<br />
                  44137 Dortmund<br />
                  Deutschland
                </p>
                <p className="mt-2 text-sm text-gray-600">
                  Firmbay ist ein Angebot der Interpohl Solutions GmbH.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Vertreten durch</h3>
                <p>Geschäftsführer: Tobias Pohl</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Kontakt</h3>
                <p>
                  Telefon: 0170-7967959<br />
                  E-Mail: info@firmbay.de
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Registereintrag</h3>
                <p>
                  Registergericht: Amtsgericht Dortmund<br />
                  Handelsregister: HRB 38151
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Umsatzsteuer-ID</h3>
                <p>
                  Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
                  USt-IdNr.: DE463318170
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Verbraucherstreitbeilegung</h3>
                <p>
                  Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
                  Verbraucherschlichtungsstelle teilzunehmen.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Hinweis zur Rolle von Firmbay</h3>
                <p className="leading-relaxed">
                  Firmbay ist ein Angebot der Interpohl Solutions GmbH und dient der Vermittlung von Gesellschaftsangeboten. 
                  Firmbay ist grundsätzlich weder Verkäufer noch Käufer der dargestellten Gesellschaften. 
                  Verträge über Gesellschaftsanteile werden unmittelbar zwischen den jeweiligen Parteien geschlossen 
                  und – soweit gesetzlich erforderlich – notariell beurkundet.
                </p>
                <p className="mt-2 leading-relaxed">
                  Firmbay erbringt keine Rechts-, Steuer- oder Anlageberatung. Angaben zu Gesellschaften beruhen 
                  grundsätzlich auf Informationen und Unterlagen der jeweiligen Anbieter. Eine eigenständige rechtliche, 
                  steuerliche oder wirtschaftliche Due-Diligence-Prüfung durch Firmbay findet nicht statt, soweit im 
                  Einzelfall nicht ausdrücklich etwas anderes vereinbart wurde.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Datenschutz */}
          <Card id="datenschutz" className="border-2 border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="text-2xl">Datenschutzerklärung</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">1. Datenschutz auf einen Blick</h3>
                <h4 className="font-medium text-gray-900 mt-3 mb-2">Allgemeine Hinweise</h4>
                <p className="leading-relaxed">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
                  Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit 
                  denen Siepersönlich identifiziert werden können.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2. Datenerfassung auf dieser Website</h3>
                <h4 className="font-medium text-gray-900 mt-3 mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h4>
                <p className="leading-relaxed">
                  Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
                  können Sie dem Impressum dieser Website entnehmen.
                </p>

                <h4 className="font-medium text-gray-900 mt-3 mb-2">Wie erfassen wir Ihre Daten?</h4>
                <p className="leading-relaxed">
                  Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich 
                  z.B. um Daten handeln, die Sie in ein Kontaktformular eingeben. Andere Daten werden automatisch 
                  beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten 
                  (z.B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
                </p>

                <h4 className="font-medium text-gray-900 mt-3 mb-2">Wofür nutzen wir Ihre Daten?</h4>
                <p className="leading-relaxed">
                  Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
                  Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">3. Kontaktformular</h3>
                <p className="leading-relaxed">
                  Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem 
                  Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der 
                  Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht 
                  ohne Ihre Einwilligung weiter.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4. Ihre Rechte</h3>
                <p className="leading-relaxed">
                  Sie haben jederzeit das Recht unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer 
                  gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung 
                  oder Löschung dieser Daten zu verlangen. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz 
                  können Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* AGB */}
          <Card id="agb" className="border-2 border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="text-2xl">Allgemeine Geschäftsbedingungen (AGB)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">1. Geltungsbereich</h3>
                <p className="leading-relaxed">
                  Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge zwischen der Interpohl Solutions GmbH 
                  (Betreiberin von Firmbay) und ihren Kunden, soweit nicht ausdrücklich etwas anderes vereinbart wurde.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2. Vertragsgegenstand</h3>
                <p className="leading-relaxed">
                  Gegenstand der Verträge ist die Vermittlung und organisatorische Begleitung bei der Gründung von 
                  Gesellschaften mit beschränkter Haftung (GmbH), bei Vorratsgesellschaften sowie bei 
                  Gesellschafterübertragungen. Die Interpohl Solutions GmbH erbringt keine Rechts- oder Steuerberatung und 
                  verkauft keine Gesellschaften.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">3. Vertragsschluss</h3>
                <p className="leading-relaxed">
                  Der Vertrag kommt durch die Annahme des Angebots des Kunden durch die Interpohl Solutions GmbH zustande. 
                  Die Annahme kann schriftlich, per E-Mail oder durch Beginn der Leistungserbringung erfolgen.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4. Vergütung und Zahlung</h3>
                <p className="leading-relaxed">
                  Die Vergütung richtet sich nach dem vereinbarten Paket. Alle Preise verstehen sich zzgl. der 
                  gesetzlichen Mehrwertsteuer. Die Zahlung ist nach Rechnungsstellung fällig.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">5. Leistungsumfang</h3>
                <p className="leading-relaxed">
                  Der Umfang der Leistungen ergibt sich aus dem gewählten Paket und der individuellen Vereinbarung. 
                  Die Interpohl Solutions GmbH erbringt ihre Leistungen nach bestem Wissen und Gewissen. Sämtliche 
                  gesellschaftsrechtlichen Vorgänge erfolgen ausschließlich durch Notare und externe, 
                  eigenverantwortliche Partner.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6. Haftung</h3>
                <p className="leading-relaxed">
                  Die Haftung der Interpohl Solutions GmbH richtet sich nach den gesetzlichen Bestimmungen. Für leichte 
                  Fahrlässigkeit haftet die Gesellschaft nur bei Verletzung wesentlicher Vertragspflichten.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">7. Gerichtsstand und anwendbares Recht</h3>
                <p className="leading-relaxed">
                  Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Dortmund, sofern der Kunde 
                  Kaufmann, juristische Person des öffentlichen Rechts oder öffentlich-rechtliches Sondervermögen ist.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
    </>
  );
};

export default Rechtliches;